/**
 * NCB Settings card
 *
 * Manages the recipient + optional CC list used by the monthly NCB
 * report "Send to NCB" action. Backed by
 * ``GET/PUT /api/reports/ncb/settings``.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Loader2, Save, Plus, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { API_BASE } from '../lib/apiBase';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function NcbSettingsCard() {
    const [recipient, setRecipient] = useState('');
    const [ccList, setCcList] = useState([]);
    const [newCc, setNewCc] = useState('');
    const [factoryName, setFactoryName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/api/reports/ncb/settings`, { headers: authHeaders() });
            setRecipient(res.data?.recipient_email || '');
            setCcList(res.data?.cc_emails || []);
            setFactoryName(res.data?.factory_name || '');
            setContactPerson(res.data?.contact_person || '');
            setContactEmail(res.data?.contact_email || '');
            setContactPhone(res.data?.contact_phone || '');
        } catch {
            setRecipient('');
            setCcList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const addCc = () => {
        const v = newCc.trim();
        if (!v) return;
        if (ccList.includes(v)) {
            toast.info('That address is already in the CC list');
            return;
        }
        if (ccList.length >= 10) {
            toast.error('Maximum 10 CC addresses');
            return;
        }
        setCcList([...ccList, v]);
        setNewCc('');
    };

    const removeCc = (v) => setCcList(ccList.filter((x) => x !== v));

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API_BASE}/api/reports/ncb/settings`,
                {
                    recipient_email: recipient.trim(),
                    cc_emails: ccList,
                    factory_name: factoryName.trim(),
                    contact_person: contactPerson.trim(),
                    contact_email: contactEmail.trim(),
                    contact_phone: contactPhone.trim(),
                },
                { headers: authHeaders() },
            );
            toast.success('NCB settings saved');
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save NCB settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card data-testid="ncb-settings-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-slate-500" />
                    NCB monthly report
                </CardTitle>
                <CardDescription>
                    Recipient for the monthly NCB report "Send to NCB" action.
                    Optional CC addresses (max 10) also receive the same email + attachment.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : (
                    <>
                        <div className="space-y-4 max-w-xl">
                            <div>
                                <h4 className="text-sm font-semibold text-slate-800 mb-1">
                                    Company info printed on the report
                                </h4>
                                <p className="text-xs text-slate-500 mb-3">
                                    These values appear in the black header block on every generated NCB xlsx
                                    (Fabrik · Kontaktperson · E-mail adresse · Telefonnummer).
                                </p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="ncb-factory">Fabrik</Label>
                                    <Input
                                        id="ncb-factory"
                                        placeholder="Owntune"
                                        value={factoryName}
                                        onChange={(e) => setFactoryName(e.target.value)}
                                        data-testid="ncb-factory-input"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="ncb-contact-person">Kontaktperson</Label>
                                    <Input
                                        id="ncb-contact-person"
                                        placeholder="Henrik Thaysen"
                                        value={contactPerson}
                                        onChange={(e) => setContactPerson(e.target.value)}
                                        data-testid="ncb-contact-person-input"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="ncb-contact-email">E-mail adresse</Label>
                                    <Input
                                        id="ncb-contact-email"
                                        type="email"
                                        placeholder="hth@owntune.com"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                        data-testid="ncb-contact-email-input"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="ncb-contact-phone">Telefonnummer</Label>
                                    <Input
                                        id="ncb-contact-phone"
                                        placeholder="+45 2077 4568"
                                        value={contactPhone}
                                        onChange={(e) => setContactPhone(e.target.value)}
                                        data-testid="ncb-contact-phone-input"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t">
                            <h4 className="text-sm font-semibold text-slate-800 mb-1">
                                Send-to-NCB recipient
                            </h4>
                            <p className="text-xs text-slate-500 mb-3">
                                Destination for the "Send to NCB" email. Attachment = the same generated xlsx.
                            </p>
                        </div>

                        <div className="space-y-1.5 max-w-xl">
                            <Label htmlFor="ncb-recipient">NCB recipient email</Label>
                            <Input
                                id="ncb-recipient"
                                type="email"
                                placeholder="rapport@ncb.dk"
                                value={recipient}
                                onChange={(e) => setRecipient(e.target.value)}
                                data-testid="ncb-recipient-input"
                            />
                        </div>

                        <div className="space-y-2 max-w-xl">
                            <Label>CC (optional)</Label>
                            <div className="flex flex-wrap gap-2">
                                {ccList.map((cc) => (
                                    <span key={cc}
                                          className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-slate-100 border border-slate-200 rounded-full text-xs">
                                        {cc}
                                        <button type="button" onClick={() => removeCc(cc)}
                                                className="p-0.5 hover:bg-slate-200 rounded-full"
                                                aria-label={`Remove ${cc}`}
                                                data-testid={`ncb-cc-remove-${cc}`}>
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                                {ccList.length === 0 && (
                                    <span className="text-xs text-slate-400 italic">No CC addresses.</span>
                                )}
                            </div>
                            <div className="flex gap-2 pt-1">
                                <Input
                                    type="email"
                                    placeholder="cc@example.com"
                                    value={newCc}
                                    onChange={(e) => setNewCc(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCc(); } }}
                                    data-testid="ncb-cc-input"
                                />
                                <Button variant="outline" onClick={addCc} disabled={!newCc.trim()}
                                        data-testid="ncb-cc-add-btn">
                                    <Plus className="h-4 w-4 mr-1" /> Add
                                </Button>
                            </div>
                        </div>

                        <div className="pt-2 border-t flex justify-end">
                            <Button onClick={save} disabled={saving} data-testid="ncb-settings-save-btn">
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save NCB settings
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
